import React, { useState } from 'react';
import { useSnackbar } from 'notistack';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Checkbox, MenuItem, Table, TableHead, TableBody,
    TableRow, TableCell, Typography, makeStyles } from '@material-ui/core';
import axios from 'axios';
import moment from 'moment';
import { formatMoney } from '../../utils';
import { typeNames, itemName } from './labels';

const useStyles = makeStyles(theme => ({
    fileRow: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: 10,
        marginBottom: 10
    },
    error: {
        color: theme.palette.error.main,
        marginTop: 10
    },
    counts: {
        margin: '8px 0'
    },
    count: {
        marginRight: 16
    },
    candidateSelect: {
        minWidth: 280,
        maxWidth: 380
    },
    Matched: {
        color: theme.palette.success.main
    },
    Suggested: {
        color: theme.palette.warning.dark
    },
    AlreadyCleared: {
        color: theme.palette.text.secondary
    },
    Unmatched: {
        color: theme.palette.error.main
    }
}));

const statusNames = { Matched: 'Matched', Suggested: 'Check', AlreadyCleared: 'Already cleared', Unmatched: 'Not found' };

function itemLabel(item) {
    return `${moment(item.date).format('DD/MM/YYYY')} · ${typeNames[item.type] || item.type} ${item.docNo} · ${itemName(item)}` +
        (item.chequeNo ? ` · Chq ${item.chequeNo}` : '');
}

// Reads a PDF bank statement, shows the transaction each line was matched to and saves the bank dates the user accepts
function StatementImportDialog({ open, onClose, onApplied, accountCode, selectedSociety }) {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();

    const [file, setFile] = useState(null);
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [reading, setReading] = useState(false);
    // Kept together so the lines are never shown without their choices, as React 16 renders after each update in a promise
    const [review, setReview] = useState({ statement: null, choices: [] });     // choices has { apply, entryId } for each line
    const { statement, choices } = review;

    function reset() {
        setFile(null);
        setPassword('');
        setError('');
        setReview({ statement: null, choices: [] });
    }

    function close() {
        reset();
        onClose();
    }

    function read() {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('password', password);

        setReading(true);
        setError('');
        axios({
            method: 'POST',
            url: '/reconciliation/statement/' + accountCode,
            headers: { 'Content-Type': 'multipart/form-data', 'X-SocietyId': selectedSociety },
            data: formData
        })
            .then(response => {
                // Suggestions are only applied once the user ticks them
                setReview({
                    statement: response.data,
                    choices: response.data.lines.map(line => ({ apply: line.status === 'Matched', entryId: line.entryId }))
                });
            })
            .catch(error => {
                setError((error.response && error.response.data && error.response.data.message) || 'The statement could not be read');
            })
            .finally(() => setReading(false));
    }

    function setChoice(index, choice) {
        setReview({ statement, choices: choices.map((x, i) => (i === index ? { ...x, ...choice } : x)) });
    }

    const candidates = statement ? Object.fromEntries(statement.candidates.map(x => [x.id, x])) : {};
    const toApply = statement ? statement.lines.map((line, i) => ({ line, choice: choices[i] })).filter(x => x.choice.apply && x.choice.entryId) : [];

    // A transaction can only be cleared by one line
    function takenElsewhere(index, entryId) {
        return choices.some((x, i) => i !== index && x.apply && x.entryId === entryId);
    }

    function apply() {
        axios({
            method: 'PUT',
            url: '/reconciliation/bankdates',
            headers: { 'X-SocietyId': selectedSociety },
            data: {
                accountCode: accountCode,
                items: toApply.map(x => ({ id: x.choice.entryId, bankDate: x.line.date }))
            }
        })
            .then(() => {
                enqueueSnackbar(`${toApply.length} transactions marked as cleared`, { variant: 'success' });
                onApplied(statement);
                reset();
            })
            .catch(error => {
                setError((error.response && error.response.data && error.response.data.message) || 'Saving failed');
            });
    }

    const counts = statement ? statement.lines.reduce((sum, line) => ({ ...sum, [line.status]: (sum[line.status] || 0) + 1 }), {}) : {};

    return (
        <Dialog open={open} onClose={close} maxWidth="lg" fullWidth>
            <DialogTitle>Import Bank Statement</DialogTitle>
            <DialogContent>
                {!statement && <>
                    <Typography variant="body2" gutterBottom>
                        Choose the statement PDF downloaded from the bank. Union Bank of India statements are supported.
                    </Typography>
                    <div className={classes.fileRow}>
                        <input type="file" accept="application/pdf,.pdf" onChange={e => { setFile(e.target.files[0] || null); setError(''); }} />
                        <TextField
                            type="password"
                            size="small"
                            label="PDF password (if any)"
                            InputLabelProps={{ shrink: true }}
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                        />
                    </div>
                </>}
                {statement && <>
                    <Typography variant="body2">
                        Statement {moment(statement.fromDate).format('DD/MM/YYYY')} to {moment(statement.toDate).format('DD/MM/YYYY')}
                        {statement.accountNumber && <> · Account {statement.accountNumber}</>}
                        {' '}· Opening {formatMoney(statement.openingBalance)} · Closing {formatMoney(statement.closingBalance)}
                    </Typography>
                    <div className={classes.counts}>
                        {Object.keys(statusNames).filter(x => counts[x]).map(x => (
                            <span key={x} className={`${classes.count} ${classes[x]}`}>{statusNames[x]}: {counts[x]}</span>
                        ))}
                    </div>
                    {counts.Unmatched > 0 && <Typography variant="body2" gutterBottom>
                        Lines not found, such as bank charges, interest or online payments, need to be entered in Transactions.
                        Importing the statement again afterwards matches them.
                    </Typography>}
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox"></TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Particulars</TableCell>
                                <TableCell align="right">Withdrawal</TableCell>
                                <TableCell align="right">Deposit</TableCell>
                                <TableCell>Transaction</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {statement.lines.map((line, index) => {
                                const choice = choices[index];
                                const canApply = line.status === 'Matched' || line.status === 'Suggested';
                                return (
                                    <TableRow key={index} hover>
                                        <TableCell padding="checkbox">
                                            {canApply && <Checkbox
                                                size="small"
                                                checked={choice.apply}
                                                disabled={!choice.entryId}
                                                onChange={e => setChoice(index, { apply: e.target.checked })}
                                            />}
                                        </TableCell>
                                        <TableCell>{moment(line.date).format('DD/MM/YYYY')}</TableCell>
                                        <TableCell>
                                            <div>{line.particulars}</div>
                                            {line.chequeNo && <div>Chq {line.chequeNo}</div>}
                                        </TableCell>
                                        <TableCell align="right">{line.withdrawal ? formatMoney(line.withdrawal) : ''}</TableCell>
                                        <TableCell align="right">{line.deposit ? formatMoney(line.deposit) : ''}</TableCell>
                                        <TableCell>
                                            {line.status === 'Suggested'
                                                ? <TextField
                                                    select
                                                    size="small"
                                                    className={classes.candidateSelect}
                                                    value={choice.entryId || ''}
                                                    onChange={e => setChoice(index, { entryId: e.target.value || null, apply: Boolean(e.target.value) })}
                                                >
                                                    <MenuItem value="">None</MenuItem>
                                                    {line.candidateIds.map(id => (
                                                        <MenuItem key={id} value={id} disabled={takenElsewhere(index, id)}>{itemLabel(candidates[id])}</MenuItem>
                                                    ))}
                                                </TextField>
                                                : line.entryId && candidates[line.entryId] ? itemLabel(candidates[line.entryId]) : ''}
                                        </TableCell>
                                        <TableCell className={classes[line.status]}>{statusNames[line.status]}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </>}
                {error && <div className={classes.error}>{error}</div>}
            </DialogContent>
            <DialogActions>
                {!statement && <Button variant="contained" color="primary" onClick={read} disabled={!file || reading}>
                    {reading ? 'Reading...' : 'Read Statement'}
                </Button>}
                {statement && <Button variant="contained" color="primary" onClick={apply} disabled={toApply.length === 0}>
                    Mark {toApply.length} as Cleared
                </Button>}
                <Button variant="contained" color="secondary" onClick={close}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
}

export default StatementImportDialog;
